import React, { useState, useEffect } from "react";
import {
  useShow,
  IResourceComponentsProps,
  useTranslate,
} from "@refinedev/core";
import { useParams } from "react-router-dom";
import {
  Show,
} from "@refinedev/mui";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  styled,
} from "@mui/material";
import { IEvent } from "./EventList";
import { format, parseISO } from "date-fns";
import LocationMap from "../../components/forms/LocationMap";

// Style personnalisé pour le Switch
const CustomSwitch = styled(Switch)(({ theme }) => ({
  '&.Mui-disabled': {
    opacity: 0.8, // Réduit l'opacité mais pas autant que la valeur par défaut
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300],
    opacity: 1,
  },
  '& .MuiSwitch-thumb': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 0 2px 1px rgba(255, 255, 255, 0.2)'
      : '0 0 2px 1px rgba(0, 0, 0, 0.2)',
  },
  '&.Mui-checked': {
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.main,
    },
    '& + .MuiFormControlLabel-label': {
      color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
      fontWeight: 600,
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.light,
    },
  },
  '&.Mui-checked.Mui-disabled + .MuiFormControlLabel-label': {
    color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
    opacity: 0.9,
  },
}));

export const EventShow: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const params = useParams();

  const { queryResult } = useShow<IEvent>();
  const { data, isLoading } = queryResult;
  const record = data?.data;

  // For map display
  const [mapCoordinates, setMapCoordinates] = useState({
    latitude: 0,
    longitude: 0,
  });

  // Update map coordinates when record loads
  useEffect(() => {
    if (record?.latitude && record?.longitude) {
      setMapCoordinates({
        latitude: record.latitude,
        longitude: record.longitude,
      });
    }
  }, [record]);

  return (
    <Show isLoading={isLoading} title={`Event: ${record?.name || ""}`}>
      <Grid container spacing={2}>
        {/* First column with event details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                  {translate("events.fields.name", "Event Name")}
                </Typography>
                <Box sx={{
                  p: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]
                }}>
                  <Typography variant="body1">{record?.name}</Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                  {translate("events.fields.description", "Description")}
                </Typography>
                <Box sx={{
                  p: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                  minHeight: '100px'
                }}>
                  <Typography variant="body1">{record?.description}</Typography>
                </Box>
              </Box>

              {/* Dates on the same row */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Start Date */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                      {translate("events.fields.startDate", "Date de début")}
                    </Typography>
                    <Box sx={{
                      p: 2,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: '4px',
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]
                    }}>
                      {record?.startDate && (
                        <Typography variant="body1">
                          {format(
                            typeof record.startDate === "string"
                              ? parseISO(record.startDate)
                              : record.startDate,
                            "dd/MM/yyyy HH:mm"
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* End Date */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                      {translate("events.fields.endDate", "Date de fin")}
                    </Typography>
                    <Box sx={{
                      p: 2,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: '4px',
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]
                    }}>
                      {record?.endDate && (
                        <Typography variant="body1">
                          {format(
                            typeof record.endDate === "string"
                              ? parseISO(record.endDate)
                              : record.endDate,
                            "dd/MM/yyyy HH:mm"
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>


              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                  {translate("isActive.title", "Status")}
                </Typography>
                <Box sx={(theme) => ({
                  p: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  bgcolor: record?.isActive
                    ? (theme.palette.mode === 'dark'
                        ? 'rgba(56, 142, 60, 0.2)'
                        : 'rgba(232, 245, 233, 0.8)')
                    : (theme.palette.mode === 'dark'
                        ? 'rgba(211, 47, 47, 0.2)'
                        : 'rgba(253, 236, 234, 0.8)'),
                  borderColor: record?.isActive
                    ? (theme.palette.mode === 'dark'
                        ? theme.palette.success.dark
                        : theme.palette.success.light)
                    : (theme.palette.mode === 'dark'
                        ? theme.palette.error.dark
                        : theme.palette.error.light)
                })}>
                  <FormControlLabel
                    sx={(theme) => ({
                      m: 0,
                      '& .MuiFormControlLabel-label': {
                        fontWeight: 'bold',
                        color: record?.isActive
                          ? (theme.palette.mode === 'dark'
                              ? theme.palette.success.light
                              : theme.palette.success.dark)
                          : (theme.palette.mode === 'dark'
                              ? theme.palette.error.light
                              : theme.palette.error.dark)
                      }
                    })}
                    label={record?.isActive ? "Active" : "Inactive"}
                    control={
                      <CustomSwitch
                        checked={record?.isActive || false}
                        disabled
                        sx={{ mr: 1 }}
                      />
                    }
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Second column with location map */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold" color="text.secondary" gutterBottom>
                  {translate("events.fields.radius", "Geofence Radius")}
                </Typography>
                <Box sx={{
                  p: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]
                }}>
                  <Typography variant="body1">{record?.geofenceRadius} meters</Typography>
                </Box>
              </Box>

              {/* Map display */}
              <Box sx={{ mt: 2, height: "400px", borderRadius: '6px' }}>
                <Box sx={{ height: "100%", borderRadius: '6px', overflow: 'hidden' }}>
                  {record && (
                    <LocationMap
                      latitude={record.latitude}
                      longitude={record.longitude}
                      radius={record.geofenceRadius}
                      canAddMarkers={false}
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Show>
  );
};
